import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useTrackPositionChange } from '../../hooks/useTrackPositionChange';
import { useEntityIdParam } from '../../hooks/useEntityIdParam';
import { useSMLDatePickerDateParam } from '../../hooks/useSMLDatePickerDateParam';
import { PositionChangeChart } from '../PositionChangeChart/PositionChangeChart';
import { ForecastBadge } from '../ForecastBadge/ForecastBadge';

interface TrackPositionChangeChartProps {
  // No props needed - component will use hooks directly
}

const TrackPositionChangeChartComponent: React.FC<TrackPositionChangeChartProps> = () => {
  const intl = useIntl();
  const trackId = useEntityIdParam('track');
  const { selectedDate, rangeDays } = useSMLDatePickerDateParam();

  const {
    data: positionChangeData,
    isLoading,
    error,
  } = useTrackPositionChange({
    trackId: trackId || '',
    date: selectedDate,
    rangeDays,
  });

  const data = React.useMemo(() => positionChangeData?.data ?? [], [positionChangeData?.data]);
  const dateRange = positionChangeData?.dateRange;
  const changePoints = React.useMemo(() => positionChangeData?.changePoints ?? [], [positionChangeData?.changePoints]);
  const forecast = positionChangeData?.forecast ?? null;

  const title = intl.formatMessage({
    id: 'app.sml.track.details.position-change',
    defaultMessage: 'Position Change',
  });

  const signedPct = React.useMemo(() => {
    if (forecast === null) {
      return '';
    }
    const pct = forecast.pctChange;
    const rounded = Math.round(Math.abs(pct) * 100);
    const sign = pct > 0 ? '+' : pct < 0 ? '-' : '';
    return `${sign}${rounded}%`;
  }, [forecast]);

  const projectedLabel =
    forecast !== null
      ? intl.formatMessage(
          {
            id: 'app.sml.track.details.projected',
            defaultMessage: 'Projected {pct} over {days}d',
          },
          {
            pct: signedPct,
            days: forecast.horizonDays,
          }
        )
      : '';

  return (
    <div>
      {forecast !== null ? (
        <ForecastBadge pctChange={forecast.pctChange} horizonDays={forecast.horizonDays} label={projectedLabel} />
      ) : null}
      <PositionChangeChart
        title={title}
        data={data}
        dateRange={dateRange}
        isLoading={isLoading}
        error={error}
        changePoints={changePoints}
      />
    </div>
  );
};

export const TrackPositionChangeChart = TypedMemo(TrackPositionChangeChartComponent);
